import React, { useRef, useEffect } from 'react';
import { Grid, Box, Tooltip, Divider } from "@mui/material";
import SettingsCard from "./SettingsCard";

const ToolbarButton = ({ onClick, title, children }) => (
  <Tooltip title={title}>
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '14px',
        color: '#344767',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '28px',
        height: '28px',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f0f2f5'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      {children}
    </button>
  </Tooltip>
);

const AddNewBusinessHeading = ({ inputData, autoSaveField, gridProps = {} }) => {
  const editorRef = useRef(null);
  const hiddenInputRef = useRef(null);

  const defaultValue =
  inputData?.add_new_business_heading ||
  "<h1 style=\"text-align: center;\"><strong>Add&nbsp;New&nbsp;Business</strong></h1>";

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = defaultValue;
    }
  }, []);

  const exec = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (hiddenInputRef.current && editorRef.current) {
      hiddenInputRef.current.value = editorRef.current.innerHTML;
    }
  };

  const handleEditorBlur = () => {
    if (!editorRef.current || !autoSaveField) return;
    autoSaveField("add_new_business_heading", editorRef.current.innerHTML);
  };

  const handleFontSize = (e) => exec('fontSize', e.target.value);
  const handleColor = (e) => exec('foreColor', e.target.value);
  const handleAlign = (align) => exec(align);

  return (
    <SettingsCard
      title="Add New Business Heading"
      subtitle="Customize Your Add new business page heading"
      gridProps={gridProps}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <input
            ref={hiddenInputRef}
            type="hidden"
            name="add_new_business_heading"
            defaultValue={defaultValue}
          />

          <Box
            sx={{
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: '8px',
              overflow: 'hidden',
              '&:focus-within': {
                borderColor: '#344767',
                boxShadow: '0 0 0 2px rgba(52,71,103,0.1)',
              },
            }}
          >
            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '2px',
                padding: '6px 8px',
                background: '#fafafa',
                borderBottom: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              {/* Text style */}
              <ToolbarButton onClick={() => exec('bold')} title="Bold">
                <b>B</b>
              </ToolbarButton>
              <ToolbarButton onClick={() => exec('italic')} title="Italic">
                <i>I</i>
              </ToolbarButton>
              <ToolbarButton onClick={() => exec('underline')} title="Underline">
                <u>U</u>
              </ToolbarButton>
              <ToolbarButton onClick={() => exec('strikeThrough')} title="Strikethrough">
                <s>S</s>
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: '4px' }} />

              {/* Font size */}
              <Tooltip title="Font Size">
                <select
                  onChange={handleFontSize}
                  defaultValue="3"
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '2px 4px',
                    fontSize: '12px',
                    color: '#344767',
                    background: 'white',
                    cursor: 'pointer',
                    height: '28px',
                  }}
                >
                  <option value="1">Small</option>
                  <option value="3">Normal</option>
                  <option value="5">Large</option>
                  <option value="7">Huge</option>
                </select>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: '4px' }} />

              {/* Alignment */}
              <ToolbarButton onClick={() => handleAlign('justifyLeft')} title="Align Left">
                ≡
              </ToolbarButton>
              <ToolbarButton onClick={() => handleAlign('justifyCenter')} title="Align Center">
                ☰
              </ToolbarButton>
              <ToolbarButton onClick={() => handleAlign('justifyRight')} title="Align Right">
                ▤
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: '4px' }} />

              {/* Lists */}
              <ToolbarButton onClick={() => exec('insertUnorderedList')} title="Bullet List">
                • List
              </ToolbarButton>
              <ToolbarButton onClick={() => exec('insertOrderedList')} title="Numbered List">
                1. List
              </ToolbarButton>

              <Divider orientation="vertical" flexItem sx={{ mx: '4px' }} />

              {/* Color */}
              <Tooltip title="Text Color">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <span style={{ fontSize: '12px', color: '#344767' }}>A</span>
                  <input
                    type="color"
                    defaultValue="#000000"
                    onChange={handleColor}
                    style={{
                      width: '24px',
                      height: '24px',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      background: 'none',
                    }}
                  />
                </Box>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: '4px' }} />

              {/* Clear */}
              <ToolbarButton onClick={() => exec('removeFormat')} title="Clear Formatting">
                ✕ Clear
              </ToolbarButton>
            </Box>

            {/* Editor Area */}
            <Box
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onBlur={handleEditorBlur}
              sx={{
                minHeight: '80px',
                padding: '12px 14px',
                fontSize: '14px',
                color: '#344767',
                outline: 'none',
                lineHeight: 1.6,
                '&:empty:before': {
                  content: '"Enter business heading..."',
                  color: '#aaa',
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </SettingsCard>
  );
};

export default AddNewBusinessHeading;